import React, { useEffect, useRef, useState } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Box,
    Divider,
    FormControl,
    Input,
    InputAdornment,
    IconButton,
    Grid2,
    RadioGroup,
    FormControlLabel,
    Radio,
} from "@mui/material";
import {
    CONVERT,
    DATE_FORMAT,
    formatDateTime,
    hasAccess,
} from "../../helper/global";
import SaveIcon from "@mui/icons-material/Save";
import { HEALTH_TYPE, PERMISSIONS } from "../../helper/constant";
import DatePickerComponent from "../DatePicker/DatePicker";
import dayjs from "dayjs";
import { set } from "date-fns";

const PatientInfo = ({
    theme,
    user,
    permissionConfig,
    patient,
    buildInputForm,
    handleOnSave,
}) => {
    const heightFtRef = useRef(null);
    const heightInRef = useRef(null);

    const [status_height, set_status_height] = useState(false);

    const [feet, setFeet] = useState("");
    const [inch, setInch] = useState("");

    const firstNameRef = useRef(null);
    const [status_first_name, set_status_first_name] = useState(false);

    const lastNameRef = useRef(null);
    const [status_last_name, set_status_last_name] = useState(false);

    const handleOnSaveHeight = () => {
        const heightFt = heightFtRef.current.value;
        const heightIn = heightInRef.current.value;

        handleOnSave(HEALTH_TYPE.HEIGHT, CONVERT.ftinToIn(heightFt, heightIn));
        set_status_height(false);
    };

    const handleOnDoBSave = (newValue) => {
        const date = newValue.format(DATE_FORMAT.defaultDate);

        handleOnSave("dob", date);
    };

    const handleOnGenderChange = (event) => {
        handleOnSave("gender", event.target.value);
    };

    const [firstName, setFirstName] = useState(patient.firstName);

    const [lastName, setLastName] = useState(patient.lastName);

    useEffect(() => {
        if (!patient) {
            return;
        }

        const { feet, inch } = patient
            ? CONVERT.inToFtIn(patient.healthData.height)
            : { feet: "", inch: "" };

        setFeet(feet);
        setInch(inch);

        setFirstName(patient.firstName);
        setLastName(patient.lastName);
    }, [patient]);

    return (
        <Card>
            <CardHeader title="Personal Information" />
            <Divider />
            <CardContent>
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                First Name
                            </Typography>
                            {buildInputForm(
                                firstNameRef,
                                "",
                                "firstName",
                                firstName,
                                status_first_name,
                                (e) => {
                                    setFirstName(e.target.value);
                                    set_status_first_name(true);
                                },
                                () => set_status_first_name(false)
                            )}
                        </Box>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Last Name
                            </Typography>
                            {buildInputForm(
                                lastNameRef,
                                "",
                                "lastName",
                                lastName,
                                status_last_name,
                                (e) => {
                                    setLastName(e.target.value);
                                    set_status_last_name(true);
                                },
                                () => set_status_last_name(false)
                            )}
                        </Box>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Email Address
                            </Typography>
                            <Typography variant="h6">
                                {patient.emailAddress}
                            </Typography>
                        </Box>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Date of birth
                            </Typography>
                            <FormControl
                                sx={{ width: "220px" }}
                                variant="standard"
                            >
                                <DatePickerComponent
                                    isDisabled={
                                        !hasAccess(
                                            user,
                                            permissionConfig,
                                            "patient",
                                            PERMISSIONS.write
                                        )
                                    }
                                    maxDate={dayjs()}
                                    onChange={handleOnDoBSave}
                                    defaultValue={
                                        patient.dob ? dayjs(patient.dob) : null
                                    }
                                />
                            </FormControl>
                        </Box>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Height
                            </Typography>
                            <Box sx={{ display: "flex" }}>
                                <Input
                                    disabled={
                                        !hasAccess(
                                            user,
                                            permissionConfig,
                                            "patient",
                                            PERMISSIONS.write
                                        )
                                    }
                                    inputRef={heightFtRef}
                                    type={"number"}
                                    value={feet}
                                    sx={{ pl: 1, width: "70px" }}
                                    inputProps={{ min: 0 }}
                                    onChange={(e) => {
                                        setFeet(e.target.value);
                                        set_status_height(true);
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {"ft"}
                                        </InputAdornment>
                                    }
                                />
                                <Input
                                    disabled={
                                        !hasAccess(
                                            user,
                                            permissionConfig,
                                            "patient",
                                            PERMISSIONS.write
                                        )
                                    }
                                    inputRef={heightInRef}
                                    type={"number"}
                                    value={inch}
                                    sx={{ ml: 1, pl: 1, width: "120px" }}
                                    inputProps={{ min: 0, max: 11 }}
                                    onChange={(e) => {
                                        setInch(e.target.value);
                                        set_status_height(true);
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {"in"}
                                            {hasAccess(
                                                user,
                                                permissionConfig,
                                                "patient",
                                                PERMISSIONS.write
                                            ) && (
                                                <IconButton
                                                    onClick={handleOnSaveHeight}
                                                >
                                                    <SaveIcon
                                                        sx={{
                                                            color: !status_height
                                                                ? theme.palette
                                                                      .color
                                                                      .icon
                                                                : "red",
                                                        }}
                                                    />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    }
                                />
                            </Box>
                        </Box>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Gender
                            </Typography>
                            <RadioGroup
                                row
                                aria-labelledby="demo-radio-buttons-group-label"
                                defaultValue={patient.gender || ""}
                                name="radio-buttons-group"
                                onChange={handleOnGenderChange}
                            >
                                <FormControlLabel
                                    value="female"
                                    control={<Radio />}
                                    label="Female"
                                />
                                <FormControlLabel
                                    value="male"
                                    control={<Radio />}
                                    label="Male"
                                />
                            </RadioGroup>
                        </Box>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Last Login
                            </Typography>
                            <Typography variant="h6">
                                {patient.lastLogin
                                    ? formatDateTime(
                                          patient.lastLogin,
                                          "MMM D, YYYY h:mm: a",
                                          false
                                      )
                                    : ""}
                            </Typography>
                        </Box>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
};

export default PatientInfo;
